import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, ArrowRight, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { api } from '../api';

export default function ContractorsPage() {
  const nav = useNavigate();
  const [state, setState] = useState({ search: '', sort: 'tickets', sort_dir: 'desc', page: 1, page_size: 25, filters: {} });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('tbc_dashboard_filters_v2');
      if (raw) setState((s) => ({ ...s, filters: JSON.parse(raw) }));
    } catch (e) {}
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    api.post('/contractors/list', state)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setError(e.response?.data?.detail || e.message || 'تعذر تحميل بيانات المقاولين'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [state]);

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / state.page_size));

  return (
    <div dir="rtl" className="min-h-screen bg-[#0A101C] flex">
      <Sidebar />
      <main className="flex-1 p-4 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-slate-400 text-[11px] font-medium">لوحة القيادة › المقاولون</div>
            <h1 className="text-white text-[22px] font-extrabold tracking-tight mt-0.5">أداء المقاولين</h1>
            <div className="text-slate-400 text-[12px] mt-0.5">{total.toLocaleString('en-US')} مقاول • مرتب حسب عدد البلاغات</div>
          </div>
          <button onClick={() => nav('/')} className="panel panel-hoverable px-3 py-2 flex items-center gap-2 text-slate-200 text-[12.5px] font-semibold">
            <ArrowRight size={14} /> العودة
          </button>
        </div>

        <div className="panel p-3 mb-3 flex items-center gap-2">
          <div className="bg-[#0B1424] border border-white/10 rounded-lg px-2.5 py-2 flex items-center gap-2 flex-1">
            <Search size={14} className="text-slate-400" />
            <input value={state.search} onChange={(e) => setState((s) => ({ ...s, search: e.target.value, page: 1 }))}
              placeholder="ابحث باسم المقاول..."
              className="bg-transparent outline-none text-slate-100 text-[13px] flex-1 min-w-0 text-right placeholder:text-slate-500" />
          </div>
          <select value={state.sort} onChange={(e) => setState((s) => ({ ...s, sort: e.target.value, page: 1 }))}
            className="bg-[#0B1424] border border-white/10 rounded-lg px-2 py-2 text-slate-100 text-[12.5px]">
            <option value="tickets">إجمالي البلاغات</option>
            <option value="completed">المكتملة</option>
            <option value="open">المفتوحة</option>
            <option value="delayed">المتأخرة</option>
            <option value="completion_rate">نسبة الإنجاز</option>
            <option value="schools">عدد المدارس</option>
          </select>
          <button onClick={() => setState((s) => ({ ...s, sort_dir: s.sort_dir === 'desc' ? 'asc' : 'desc', page: 1 }))}
            className="panel panel-hoverable px-3 py-2 text-slate-200 text-[12.5px] font-semibold">{state.sort_dir === 'desc' ? 'تنازلي ↓' : 'تصاعدي ↑'}</button>
        </div>

        <div className="panel p-0 overflow-hidden">
          {loading && <div className="p-8 flex items-center justify-center gap-2 text-slate-300"><Loader2 size={16} className="animate-spin text-blue-400" /> جاري التحميل...</div>}
          {!loading && error && (
            <div className="p-6 text-center text-amber-300 text-sm">تعذر تحميل بيانات المقاولين — {error}</div>
          )}
          {!loading && !error && (
            <div className="overflow-x-auto">
              <table className="w-full text-[12px] text-right">
                <thead className="bg-white/[0.03] border-b border-white/[0.06]">
                  <tr className="text-slate-300">
                    <th className="py-2 px-2 font-semibold">#</th>
                    <th className="py-2 px-2 font-semibold">المقاول</th>
                    <th className="py-2 px-2 font-semibold text-center">إجمالي</th>
                    <th className="py-2 px-2 font-semibold text-center">مكتملة</th>
                    <th className="py-2 px-2 font-semibold text-center">مفتوحة</th>
                    <th className="py-2 px-2 font-semibold text-center">متأخرة</th>
                    <th className="py-2 px-2 font-semibold text-center">نسبة الإنجاز</th>
                    <th className="py-2 px-2 font-semibold text-center">مدارس</th>
                    <th className="py-2 px-2 font-semibold text-center">محافظات</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.name} className="border-b border-white/[0.03] hover:bg-white/[0.03]">
                      <td className="py-2 px-2 text-slate-500 tabular-nums">{(state.page - 1) * state.page_size + i + 1}</td>
                      <td className="py-2 px-2 text-slate-100 font-semibold truncate max-w-[220px]" title={r.name}>{r.name}</td>
                      <td className="py-2 px-2 text-center"><span className="pill pill-blue tabular-nums">{r.tickets.toLocaleString('en-US')}</span></td>
                      <td className="py-2 px-2 text-center text-slate-100 font-semibold tabular-nums">{r.completed.toLocaleString('en-US')}</td>
                      <td className="py-2 px-2 text-center"><span className={`pill tabular-nums ${r.open > 0 ? 'pill-amber' : ''}`}>{r.open}</span></td>
                      <td className="py-2 px-2 text-center"><span className={`pill tabular-nums ${r.delayed > 0 ? 'pill-red' : ''}`}>{r.delayed}</span></td>
                      <td className="py-2 px-2 text-center"><span className={`pill tabular-nums ${r.completion_rate >= 90 ? 'pill-green' : r.completion_rate >= 60 ? 'pill-blue' : 'pill-amber'}`}>{r.completion_rate}%</span></td>
                      <td className="py-2 px-2 text-center text-slate-300 tabular-nums">{r.schools}</td>
                      <td className="py-2 px-2 text-center text-slate-400 tabular-nums">{r.governorates}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/5">
            <div className="text-slate-400 text-[12px]">الصفحة <b className="text-slate-100">{state.page}</b> من <b className="text-slate-100">{totalPages}</b></div>
            <div className="flex items-center gap-2">
              <button disabled={state.page <= 1} onClick={() => setState((s) => ({ ...s, page: s.page - 1 }))} className="panel panel-hoverable w-9 h-9 flex items-center justify-center text-slate-200 disabled:opacity-40"><ChevronRight size={14} /></button>
              <button disabled={state.page >= totalPages} onClick={() => setState((s) => ({ ...s, page: s.page + 1 }))} className="panel panel-hoverable w-9 h-9 flex items-center justify-center text-slate-200 disabled:opacity-40"><ChevronLeft size={14} /></button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
